import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AddressInput, cleanupPacContainers } from "@/components/form-fields/address-input";
import { AddressCard } from "@/components/form-fields/address-card";
import { Plus, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Address, ContactAddress, AccountAddress } from "@shared/schema";

type EntityType = "contact" | "account";

interface AddressPanelProps {
  entityType: EntityType;
  entityId: string;
  "data-testid"?: string;
}

interface AddressFormData {
  unitNumber?: string;
  streetNumber?: string;
  streetName?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  formattedAddress?: string;
  latitude?: string;
  longitude?: string;
  placeId?: string;
  isVerified?: number;
}

const addressTypes = [
  { value: "residential", label: "Residential" },
  { value: "postal", label: "Postal" },
  { value: "registered_office", label: "Registered Office" },
  { value: "principal_place_of_business", label: "Principal Place of Business" },
  { value: "property", label: "Property" },
  { value: "security", label: "Security" },
];

type AddressWithJunction = (ContactAddress | AccountAddress) & { address: Address };

export function AddressPanel({
  entityType,
  entityId,
  "data-testid": testId,
}: AddressPanelProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AddressWithJunction | null>(null);
  const [selectedAddressType, setSelectedAddressType] = useState("residential");
  const [addressData, setAddressData] = useState<AddressFormData | null>(null);
  const [notes, setNotes] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [useManualEntry, setUseManualEntry] = useState(false);
  const [hasGoogleApi, setHasGoogleApi] = useState(false);
  
  // Manual entry form fields
  const [manualAddress, setManualAddress] = useState({
    unitNumber: "",
    streetNumber: "",
    streetName: "",
    suburb: "",
    state: "",
    postcode: "",
    country: "Australia",
  });
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Check if Google Maps API key is configured
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    setHasGoogleApi(!!apiKey);
    if (!apiKey) {
      setUseManualEntry(true);
    }
  }, []);

  const queryKey =
    entityType === "contact"
      ? ["/api/crm/contacts", entityId, "addresses"]
      : ["/api/crm/accounts", entityId, "addresses"];

  const apiPath =
    entityType === "contact"
      ? `/api/crm/contacts/${entityId}/addresses`
      : `/api/crm/accounts/${entityId}/addresses`;

  const { data: addresses, isLoading } = useQuery<AddressWithJunction[]>({
    queryKey,
  });

  const createAddressMutation = useMutation({
    mutationFn: async (data: {
      address: AddressFormData;
      addressType: string;
      notes: string;
      isPrimary: boolean;
    }) => {
      const addressResponse = await apiRequest("POST", "/api/crm/addresses", data.address);
      const newAddress = await addressResponse.json();

      const junctionData = {
        addressId: newAddress.id,
        addressType: data.addressType,
        isPrimary: data.isPrimary ? 1 : 0,
        notes: data.notes || null,
      };

      return apiRequest("POST", apiPath, junctionData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setIsAddModalOpen(false);
      resetForm();
      toast({
        title: "Address added",
        description: "The address has been added successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add address",
        variant: "destructive",
      });
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: async (junctionId: string) => {
      const endpoint =
        entityType === "contact"
          ? `/api/crm/contact-addresses/${junctionId}`
          : `/api/crm/account-addresses/${junctionId}`;
      return apiRequest("DELETE", endpoint);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: "Address removed",
        description: "The address has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove address",
        variant: "destructive",
      });
    },
  });

  const setPrimaryMutation = useMutation({
    mutationFn: async ({
      junctionId,
      addressType,
    }: {
      junctionId: string;
      addressType: string;
    }) => {
      return apiRequest("POST", `${apiPath}/${junctionId}/set-primary`, {
        addressType,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: "Primary address updated",
        description: "The primary address has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to set primary address",
        variant: "destructive",
      });
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: async (data: {
      addressId: string;
      junctionId: string;
      address: AddressFormData;
      addressType: string;
      notes: string;
      isPrimary: boolean;
    }) => {
      // Update the address record
      await apiRequest("PATCH", `/api/crm/addresses/${data.addressId}`, data.address);

      // Update the junction record
      const junctionEndpoint =
        entityType === "contact"
          ? `/api/crm/contact-addresses/${data.junctionId}`
          : `/api/crm/account-addresses/${data.junctionId}`;

      const junctionData = {
        addressType: data.addressType,
        isPrimary: data.isPrimary ? 1 : 0,
        notes: data.notes || null,
      };

      return apiRequest("PATCH", junctionEndpoint, junctionData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setIsEditModalOpen(false);
      setEditingItem(null);
      resetForm();
      toast({
        title: "Address updated",
        description: "The address has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update address",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setSelectedAddressType("residential");
    setAddressData(null);
    setNotes("");
    setIsPrimary(false);
    setManualAddress({
      unitNumber: "",
      streetNumber: "",
      streetName: "",
      suburb: "",
      state: "",
      postcode: "",
      country: "Australia",
    });
    if (!hasGoogleApi) {
      setUseManualEntry(true);
    } else {
      setUseManualEntry(false);
    }
  };

  const handleAddressSelect = (data: AddressFormData) => {
    setAddressData(data);
  };
  
  const buildFormattedAddress = () => {
    const parts: string[] = [];
    
    // Build street line with proper handling of optional fields
    const streetParts: string[] = [];
    if (manualAddress.unitNumber?.trim()) {
      streetParts.push(`${manualAddress.unitNumber.trim()}/`);
    }
    if (manualAddress.streetNumber?.trim()) {
      streetParts.push(manualAddress.streetNumber.trim());
    }
    if (manualAddress.streetName?.trim()) {
      if (streetParts.length > 0) {
        streetParts.push(` ${manualAddress.streetName.trim()}`);
      } else {
        streetParts.push(manualAddress.streetName.trim());
      }
    }
    if (streetParts.length > 0) {
      parts.push(streetParts.join(""));
    }
    
    // Build location line
    const locationParts = [
      manualAddress.suburb?.trim(),
      manualAddress.state?.trim(),
      manualAddress.postcode?.trim(),
    ].filter(Boolean);
    
    if (locationParts.length > 0) {
      parts.push(locationParts.join(" "));
    }
    
    // Add country only if not Australia
    if (manualAddress.country?.trim() && manualAddress.country.trim().toLowerCase() !== "australia") {
      parts.push(manualAddress.country.trim());
    }
    
    return parts.join(", ");
  };
  
  const isManualAddressValid = () => {
    return (
      manualAddress.streetName.trim() !== "" &&
      manualAddress.suburb.trim() !== "" &&
      manualAddress.state.trim() !== "" &&
      manualAddress.postcode.trim() !== ""
    );
  };

  const handleEditOpen = (item: AddressWithJunction) => {
    setEditingItem(item);
    setAddressData(null);
    setSelectedAddressType(item.addressType);
    setNotes(item.notes || "");
    setIsPrimary(item.isPrimary === 1);
    setManualAddress({
      unitNumber: item.address.unitNumber || "",
      streetNumber: item.address.streetNumber || "",
      streetName: item.address.streetName || "",
      suburb: item.address.suburb || "",
      state: item.address.state || "",
      postcode: item.address.postcode || "",
      country: item.address.country || "Australia",
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = () => {
    if (!editingItem || updateAddressMutation.isPending) return;

    // If user selected a Google Place, use that data (isVerified=1 from AddressInput)
    if (addressData) {
      updateAddressMutation.mutate({
        addressId: editingItem.address.id,
        junctionId: editingItem.id,
        address: addressData,
        addressType: selectedAddressType,
        notes,
        isPrimary,
      });
      return;
    }

    // Otherwise submit manually-edited fields with isVerified: 0
    if (!isManualAddressValid()) {
      toast({
        title: "Incomplete address",
        description: "Please fill in all required address fields.",
        variant: "destructive",
      });
      return;
    }

    const formattedAddress = buildFormattedAddress();
    updateAddressMutation.mutate({
      addressId: editingItem.address.id,
      junctionId: editingItem.id,
      address: {
        unitNumber: manualAddress.unitNumber || undefined,
        streetNumber: manualAddress.streetNumber || undefined,
        streetName: manualAddress.streetName || undefined,
        suburb: manualAddress.suburb || undefined,
        state: manualAddress.state || undefined,
        postcode: manualAddress.postcode || undefined,
        country: manualAddress.country || "Australia",
        formattedAddress,
        isVerified: 0,
        placeId: undefined, // Drop placeId on manual edit
      },
      addressType: selectedAddressType,
      notes,
      isPrimary,
    });
  };

  const handleSubmit = () => {
    if (useManualEntry) {
      if (!isManualAddressValid()) {
        toast({
          title: "Incomplete address",
          description: "Please fill in all required address fields.",
          variant: "destructive",
        });
        return;
      }
      
      const formattedAddress = buildFormattedAddress();
      createAddressMutation.mutate({
        address: {
          unitNumber: manualAddress.unitNumber || undefined,
          streetNumber: manualAddress.streetNumber || undefined,
          streetName: manualAddress.streetName || undefined,
          suburb: manualAddress.suburb || undefined,
          state: manualAddress.state || undefined,
          postcode: manualAddress.postcode || undefined,
          country: manualAddress.country || "Australia",
          formattedAddress,
          isVerified: 0,
        },
        addressType: selectedAddressType,
        notes,
        isPrimary,
      });
    } else {
      if (!addressData) {
        toast({
          title: "No address selected",
          description: "Please search and select an address.",
          variant: "destructive",
        });
        return;
      }

      createAddressMutation.mutate({
        address: addressData,
        addressType: selectedAddressType,
        notes,
        isPrimary,
      });
    }
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium">Addresses</h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsAddModalOpen(true)}
          data-testid="button-add-address"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : addresses?.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No addresses added yet.
        </p>
      ) : (
        <div className="space-y-2">
          {addresses?.map((item) => (
            <AddressCard
              key={item.id}
              address={item.address}
              junction={item}
              isPrimary={item.isPrimary === 1}
              onEdit={() => handleEditOpen(item)}
              onDelete={() => deleteAddressMutation.mutate(item.id)}
              onSetPrimary={() =>
                setPrimaryMutation.mutate({
                  junctionId: item.id,
                  addressType: item.addressType,
                })
              }
              data-testid={`card-address-${item.id}`}
            />
          ))}
        </div>
      )}

      <Dialog open={isAddModalOpen} onOpenChange={(open) => {
        setIsAddModalOpen(open);
        // ISSUE-054 Rework #3: Clean up pac-containers when modal closes
        if (!open) {
          cleanupPacContainers();
        }
      }}>
        <DialogContent
          className="sm:max-w-[500px]"
          onInteractOutside={(e) => {
            // Prevent dialog from closing when clicking Google Places autocomplete
            const target = e.target as HTMLElement;
            if (target.closest('.pac-container')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Add Address</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Address Type</Label>
              <Select
                value={selectedAddressType}
                onValueChange={setSelectedAddressType}
              >
                <SelectTrigger data-testid="select-address-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {addressTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Toggle between search and manual entry */}
            {hasGoogleApi && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="manualEntry"
                  checked={useManualEntry}
                  onChange={(e) => setUseManualEntry(e.target.checked)}
                  className="rounded border-gray-300"
                  data-testid="checkbox-manual-entry"
                />
                <Label htmlFor="manualEntry" className="cursor-pointer text-sm">
                  Enter address manually
                </Label>
              </div>
            )}

            {useManualEntry ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Unit Number</Label>
                    <Input
                      value={manualAddress.unitNumber}
                      onChange={(e) => setManualAddress({ ...manualAddress, unitNumber: e.target.value })}
                      placeholder="e.g., 5"
                      data-testid="input-unit-number"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Street Number</Label>
                    <Input
                      value={manualAddress.streetNumber}
                      onChange={(e) => setManualAddress({ ...manualAddress, streetNumber: e.target.value })}
                      placeholder="e.g., 123"
                      data-testid="input-street-number"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Street Name *</Label>
                  <Input
                    value={manualAddress.streetName}
                    onChange={(e) => setManualAddress({ ...manualAddress, streetName: e.target.value })}
                    placeholder="e.g., Main Street"
                    data-testid="input-street-name"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Suburb *</Label>
                    <Input
                      value={manualAddress.suburb}
                      onChange={(e) => setManualAddress({ ...manualAddress, suburb: e.target.value })}
                      placeholder="e.g., Sydney"
                      data-testid="input-suburb"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">State *</Label>
                    <Input
                      value={manualAddress.state}
                      onChange={(e) => setManualAddress({ ...manualAddress, state: e.target.value })}
                      placeholder="e.g., NSW"
                      data-testid="input-state"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Postcode *</Label>
                    <Input
                      value={manualAddress.postcode}
                      onChange={(e) => setManualAddress({ ...manualAddress, postcode: e.target.value })}
                      placeholder="e.g., 2000"
                      data-testid="input-postcode"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Country</Label>
                  <Input
                    value={manualAddress.country}
                    onChange={(e) => setManualAddress({ ...manualAddress, country: e.target.value })}
                    placeholder="Australia"
                    data-testid="input-country"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Search Address</Label>
                <AddressInput
                  onAddressSelect={handleAddressSelect}
                  placeholder="Start typing to search..."
                  data-testid="input-address-search"
                />
                {addressData && (
                  <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                    Selected: {addressData.formattedAddress || "Address parsed"}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this address..."
                rows={2}
                data-testid="input-address-notes"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPrimary"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="isPrimary" className="cursor-pointer">
                Set as primary address for this type
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAddModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={(useManualEntry ? !isManualAddressValid() : !addressData) || createAddressMutation.isPending}
              data-testid="button-save-address"
            >
              {createAddressMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Address"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => {
        setIsEditModalOpen(open);
        if (!open) {
          setEditingItem(null);
          resetForm();
          cleanupPacContainers();
        }
      }}>
        <DialogContent
          className="sm:max-w-[500px]"
          onInteractOutside={(e) => {
            // Prevent dialog from closing when clicking Google Places autocomplete
            const target = e.target as HTMLElement;
            if (target.closest('.pac-container')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit Address</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Address Type</Label>
              <Select
                value={selectedAddressType}
                onValueChange={setSelectedAddressType}
              >
                <SelectTrigger data-testid="select-edit-address-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {addressTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Google Places search — always visible in Edit mode */}
            {hasGoogleApi && (
              <div className="space-y-2">
                <Label>Search Address</Label>
                <AddressInput
                  onAddressSelect={(data) => {
                    handleAddressSelect(data);
                    // Overwrite manual fields with Google result
                    setManualAddress({
                      unitNumber: data.unitNumber || "",
                      streetNumber: data.streetNumber || "",
                      streetName: data.streetName || "",
                      suburb: data.suburb || "",
                      state: data.state || "",
                      postcode: data.postcode || "",
                      country: data.country || "Australia",
                    });
                  }}
                  placeholder="Search to update address..."
                  data-testid="input-edit-address-search"
                />
                {addressData && (
                  <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                    Selected: {addressData.formattedAddress || "Address parsed"}
                  </div>
                )}
              </div>
            )}

            {/* Manual address fields — always visible and pre-populated in Edit mode */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Unit Number</Label>
                  <Input
                    value={manualAddress.unitNumber}
                    onChange={(e) => {
                      setManualAddress({ ...manualAddress, unitNumber: e.target.value });
                      setAddressData(null); // Clear Google selection on manual edit
                    }}
                    placeholder="e.g., 5"
                    data-testid="input-edit-unit-number"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Street Number</Label>
                  <Input
                    value={manualAddress.streetNumber}
                    onChange={(e) => {
                      setManualAddress({ ...manualAddress, streetNumber: e.target.value });
                      setAddressData(null);
                    }}
                    placeholder="e.g., 123"
                    data-testid="input-edit-street-number"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Street Name *</Label>
                <Input
                  value={manualAddress.streetName}
                  onChange={(e) => {
                    setManualAddress({ ...manualAddress, streetName: e.target.value });
                    setAddressData(null);
                  }}
                  placeholder="e.g., Main Street"
                  data-testid="input-edit-street-name"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Suburb *</Label>
                  <Input
                    value={manualAddress.suburb}
                    onChange={(e) => {
                      setManualAddress({ ...manualAddress, suburb: e.target.value });
                      setAddressData(null);
                    }}
                    placeholder="e.g., Sydney"
                    data-testid="input-edit-suburb"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">State *</Label>
                  <Input
                    value={manualAddress.state}
                    onChange={(e) => {
                      setManualAddress({ ...manualAddress, state: e.target.value });
                      setAddressData(null);
                    }}
                    placeholder="e.g., NSW"
                    data-testid="input-edit-state"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Postcode *</Label>
                  <Input
                    value={manualAddress.postcode}
                    onChange={(e) => {
                      setManualAddress({ ...manualAddress, postcode: e.target.value });
                      setAddressData(null);
                    }}
                    placeholder="e.g., 2000"
                    data-testid="input-edit-postcode"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Country</Label>
                <Input
                  value={manualAddress.country}
                  onChange={(e) => {
                    setManualAddress({ ...manualAddress, country: e.target.value });
                    setAddressData(null);
                  }}
                  placeholder="Australia"
                  data-testid="input-edit-country"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this address..."
                rows={2}
                data-testid="input-edit-address-notes"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="editIsPrimary"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="editIsPrimary" className="cursor-pointer">
                Set as primary address for this type
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingItem(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleEditSubmit}
              disabled={!isManualAddressValid() || updateAddressMutation.isPending}
              data-testid="button-update-address"
            >
              {updateAddressMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Address"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
